import { useEffect, useState } from "react";
import WindowSearch from "@/components/WindowSearch";
import { toast } from "sonner";
import { Loader2, RefreshCw, ShieldCheck, ShieldAlert, Zap, DollarSign, KeyRound, ExternalLink, Play, CheckCircle2, BookOpen, X } from "lucide-react";
import { api } from "@/lib/api";
import CustomProviders from "@/components/CustomProviders";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function InstagramProviders() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(null);   // provider id being tested
  const [testImg, setTestImg] = useState(null);   // { id, url }
  const [health, setHealth] = useState(null);      // { [pid]: statusObj }
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [keyDraft, setKeyDraft] = useState({});     // { "pid.ENV": value }
  const [savingKeys, setSavingKeys] = useState(null);
  const [liveModels, setLiveModels] = useState({}); // { pid: [models] }
  const [refreshingModels, setRefreshingModels] = useState(null);
  const [usage, setUsage] = useState(null);
  const [guideFor, setGuideFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [cfg, use] = await Promise.all([api.igProvidersGet(), api.igProvidersUsage().catch(() => null)]);
      setData(cfg); if (use) setUsage(use);
    } catch { toast.error("Could not load AI providers."); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const refreshModels = async (pid) => {
    setRefreshingModels(pid);
    try {
      const r = await api.igProviderModels(pid);
      setLiveModels((prev) => ({ ...prev, [pid]: r.models }));
      toast.success(`${r.models.length} model(s) found (${r.source})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Model refresh failed"); }
    setRefreshingModels(null);
  };

  const runHealth = async () => {
    setCheckingHealth(true);
    try {
      const r = await api.igProvidersHealth();
      const map = {};
      (r.providers || []).forEach((p) => { map[p.provider] = p; });
      setHealth(map);
      const ready = (r.providers || []).filter((p) => p.status === "READY").length;
      toast.success(`Health check done — ${ready} provider(s) READY`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Health check failed"); }
    setCheckingHealth(false);
  };

  const findFree = async () => {
    setCheckingHealth(true);
    try {
      const r = await api.igProvidersFindFree();
      const map = {};
      (r.results || []).forEach((p) => { map[p.provider] = p; });
      setHealth((prev) => ({ ...(prev || {}), ...map }));
      if (r.chosen) toast.success(`Active free provider: ${r.chosen}`);
      else toast.error(r.message || "No verified free provider available");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Find free provider failed"); }
    setCheckingHealth(false);
  };

  const saveKeys = async (p) => {
    const envs = [...(p.key_envs || []), ...(p.optional_key_env ? [p.optional_key_env] : [])];
    const values = {};
    envs.forEach((ev) => { const v = keyDraft[`${p.id}.${ev}`]; if (v && v.trim()) values[ev] = v.trim(); });
    if (Object.keys(values).length === 0) { toast.error("Enter at least one key value."); return; }
    setSavingKeys(p.id);
    try {
      const r = await api.igProviderSaveKeys(p.id, values);
      setData(r.view);
      // clear the drafts we just saved
      setKeyDraft((prev) => { const n = { ...prev }; envs.forEach((ev) => delete n[`${p.id}.${ev}`]); return n; });
      toast.success(`Saved ${r.saved.length} key(s) for ${p.name}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save keys"); }
    setSavingKeys(null);
  };

  const save = async (patch, successMsg) => {
    setSaving(true);
    try {
      const r = await api.igProvidersSave(patch);
      setData(r);
      if (successMsg) toast.success(successMsg);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const setProvider = (id, field, value) =>
    save({ providers: { [id]: { [field]: value } } });

  const runTest = async (id) => {
    setTesting(id); setTestImg(null);
    try {
      const r = await api.igProviderTest(id);
      setTestImg({ id, url: `${BACKEND}${r.url}` });
      toast.success(`${id} test succeeded in ${r.ms}ms`);
    } catch (e) { toast.error(e?.response?.data?.detail || `${id} test failed`); }
    setTesting(null);
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#E1306C]" /></div>;
  if (!data) return null;

  const freeFirst = data.cost_mode === "free_first";
  const freeEnabled = data.providers.filter((p) => p.cost_tier !== "paid" && p.enabled).length;
  const paidBlockedNow = freeFirst && freeEnabled === 0 && data.providers.some((p) => p.enabled);

  return (
    <div data-testid="instagram-providers">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-black text-white">Instagram AI Image Providers</h2>
          <p className="max-w-2xl text-xs text-[#A1A1AA]">Route post generation through free providers first so testing never drains Emergent credits. Paid providers are hard-blocked while FREE-FIRST is on.</p>
        </div>
        <button onClick={load} data-testid="igp-refresh" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:text-white"><RefreshCw size={13} /> Refresh</button>
        <button onClick={runHealth} disabled={checkingHealth} data-testid="igp-health-check" className="ml-2 flex items-center gap-1.5 rounded-lg border border-[#00F0FF]/40 bg-[#00F0FF]/[0.06] px-3 py-1.5 text-xs font-bold text-[#00F0FF] hover:bg-[#00F0FF]/[0.12] disabled:opacity-50">{checkingHealth ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />} Run Health Check</button>
        <button onClick={findFree} disabled={checkingHealth} data-testid="igp-find-free" className="ml-2 flex items-center gap-1.5 rounded-lg border border-[#30D158]/40 bg-[#30D158]/[0.08] px-3 py-1.5 text-xs font-bold text-[#30D158] hover:bg-[#30D158]/[0.16] disabled:opacity-50">{checkingHealth ? <Loader2 size={13} className="animate-spin" /> : <Zap size={13} />} Find Working Free Provider</button>
      </div>

      {/* Cost mode toggle */}
      <div className={`mb-4 rounded-xl border p-4 ${freeFirst ? "border-[#30D158]/40 bg-[#30D158]/[0.06]" : "border-[#FF9F0A]/40 bg-[#FF9F0A]/[0.06]"}`}>
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {freeFirst ? <ShieldCheck className="text-[#30D158]" /> : <ShieldAlert className="text-[#FF9F0A]" />}
            <div>
              <div className="font-display text-sm font-black text-white">Cost Safety Mode</div>
              <div className="text-xs text-[#A1A1AA]">{freeFirst
                ? "FREE-FIRST is ON — paid providers (Emergent / fal) are blocked."
                : "ALLOW PAID is ON — paid providers may run and consume Emergent credits."}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => save({ cost_mode: "free_first" }, "FREE-FIRST mode on")} disabled={saving}
              data-testid="igp-mode-free" className={`rounded-lg px-3 py-1.5 text-xs font-bold ${freeFirst ? "bg-[#30D158] text-black" : "border border-white/15 text-[#A1A1AA]"}`}>FREE-FIRST</button>
            <button onClick={() => save({ cost_mode: "allow_paid" }, "Paid providers allowed")} disabled={saving}
              data-testid="igp-mode-paid" className={`rounded-lg px-3 py-1.5 text-xs font-bold ${!freeFirst ? "bg-[#FF9F0A] text-black" : "border border-white/15 text-[#A1A1AA]"}`}>ALLOW PAID</button>
          </div>
        </div>
      </div>

      {paidBlockedNow && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-[#FF453A]/30 bg-[#FF453A]/[0.08] px-3 py-2 text-xs text-[#FF8A80]" data-testid="igp-blocked-warning">
          <ShieldAlert size={14} /> No free provider is enabled — generation will be blocked. Enable a free provider below (or switch to ALLOW PAID).
        </div>
      )}

      {usage && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="igp-usage-dashboard">
          <div className="rounded-xl border border-[#30D158]/30 bg-[#30D158]/[0.06] p-3">
            <div className="text-2xl font-black text-[#30D158]" data-testid="igp-usage-avoided">{usage.totals.paid_generations_avoided}</div>
            <div className="text-[11px] text-[#A1A1AA]">Paid generations avoided (FREE-FIRST win)</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-2xl font-black text-white">{usage.totals.free_success}</div>
            <div className="text-[11px] text-[#A1A1AA]">Free generations</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-2xl font-black text-[#FF9F0A]">{usage.totals.paid_success}</div>
            <div className="text-[11px] text-[#A1A1AA]">Paid generations</div>
          </div>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
            <div className="text-2xl font-black text-white">{usage.totals.total_success}</div>
            <div className="text-[11px] text-[#A1A1AA]">Total successful</div>
          </div>
          {usage.providers.length > 0 && (
            <div className="col-span-2 flex flex-wrap gap-2 sm:col-span-4">
              {usage.providers.map((p) => (
                <span key={p.provider} className="rounded-lg border border-white/10 bg-black/30 px-2.5 py-1 text-[11px] text-[#A1A1AA]">
                  <b className="text-white">{p.name}</b> <span className={p.cost_tier === "free" ? "text-[#30D158]" : "text-[#FF9F0A]"}>({p.cost_tier})</span> · {p.success}✓{p.failed ? ` / ${p.failed}✗` : ""}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Selection + fallback */}
      <div className="mb-5 grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#666]">Primary provider</div>
          <select value={data.selection} onChange={(e) => save({ selection: e.target.value })} disabled={saving}
            data-testid="igp-selection" className="w-full rounded-lg border border-white/10 bg-[#121212] px-3 py-2 text-sm text-white">
            <option value="auto">Auto (by priority)</option>
            {data.providers.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="mb-1 text-[11px] font-bold uppercase tracking-wide text-[#666]">Fallback chain</div>
          <label className="flex cursor-pointer items-center gap-2 py-2 text-sm text-white">
            <input type="checkbox" checked={!!data.fallback_enabled} data-testid="igp-fallback"
              onChange={(e) => save({ fallback_enabled: e.target.checked })} disabled={saving} />
            Try other enabled providers if the primary fails
          </label>
        </div>
      </div>

      {/* Provider list */}
      <div className="space-y-3">
        {data.providers.map((p) => {
          const blocked = freeFirst && p.cost_tier === "paid";
          return (
          <div key={p.id} data-testid={`igp-card-${p.id}`}
            className={`rounded-xl border p-4 ${blocked ? "border-white/5 bg-white/[0.01] opacity-60" : "border-white/10 bg-white/[0.02]"}`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-display text-base font-black text-white">{p.name}</span>
                  {p.cost_tier === "free"
                    ? <span className="flex items-center gap-1 rounded-full bg-[#30D158]/15 px-2 py-0.5 text-[10px] font-bold text-[#30D158]"><Zap size={10} /> FREE</span>
                    : p.cost_tier === "byok_paid"
                    ? <span className="flex items-center gap-1 rounded-full bg-[#00F0FF]/15 px-2 py-0.5 text-[10px] font-bold text-[#00F0FF]"><DollarSign size={10} /> YOUR KEY</span>
                    : <span className="flex items-center gap-1 rounded-full bg-[#FF9F0A]/15 px-2 py-0.5 text-[10px] font-bold text-[#FF9F0A]"><DollarSign size={10} /> PAID</span>}
                  {p.supports_transparent && <span className="rounded-full bg-[#8B5CF6]/15 px-2 py-0.5 text-[10px] font-bold text-[#B794F6]">alpha</span>}
                  {p.supports_reference && <span className="rounded-full bg-[#00F0FF]/12 px-2 py-0.5 text-[10px] font-bold text-[#00F0FF]">reference</span>}
                  {data.selection === p.id && <span data-testid={`igp-active-${p.id}`} className="rounded-full bg-[#00F0FF]/20 px-2 py-0.5 text-[10px] font-bold text-[#00F0FF]">ACTIVE</span>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => setGuideFor(p)} data-testid={`igp-guide-${p.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-[#A1A1AA] hover:border-[#8B5CF6]/60 hover:text-white">
                  <BookOpen size={12} /> Setup Guide
                </button>
                <button onClick={() => runTest(p.id)} disabled={testing === p.id || !p.keys_configured}
                  data-testid={`igp-test-${p.id}`}
                  className="flex items-center gap-1.5 rounded-lg border border-white/15 px-2.5 py-1.5 text-xs font-semibold text-white hover:border-[#00F0FF]/60 disabled:opacity-40">
                  {testing === p.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />} Test
                </button>
                <label className="flex cursor-pointer items-center gap-2 text-xs font-bold text-white">
                  <input type="checkbox" checked={!!p.enabled} disabled={saving || blocked}
                    data-testid={`igp-enable-${p.id}`}
                    onChange={(e) => setProvider(p.id, "enabled", e.target.checked)} />
                  {p.enabled ? "Enabled" : "Disabled"}
                </label>
              </div>
            </div>
            <p className="mt-1 text-xs text-[#A1A1AA]">{p.note}</p>
            {health && health[p.id] && (
              <div data-testid={`igp-health-${p.id}`} className="mt-2 flex flex-wrap items-center gap-2 rounded-lg border border-white/10 bg-black/30 px-2.5 py-1.5 text-[11px]">
                <span className={`rounded px-1.5 py-0.5 font-bold ${health[p.id].status === "READY" ? "bg-[#30D158]/20 text-[#30D158]" : health[p.id].status === "UNCONFIGURED" ? "bg-white/10 text-[#A1A1AA]" : "bg-[#FF453A]/20 text-[#FF8A80]"}`}>{health[p.id].status}</span>
                {health[p.id].generationTest !== "NOT_RUN" && <span className="text-[#A1A1AA]">gen: <b className={health[p.id].generationTest === "PASS" ? "text-[#30D158]" : "text-[#FF8A80]"}>{health[p.id].generationTest}</b></span>}
                {health[p.id].latencyMs != null && <span className="text-[#666]">{health[p.id].latencyMs}ms</span>}
                {health[p.id].providerErrorCode && <span className="text-[#FF9F0A]">code {health[p.id].providerErrorCode}</span>}
                {health[p.id].safeErrorMessage && <span className="text-[#FF8A80]">{health[p.id].safeErrorMessage}</span>}
                {health[p.id].quota && (
                  <span data-testid={`igp-quota-${p.id}`} className="ml-1 rounded bg-white/5 px-1.5 py-0.5 text-[#A1A1AA]">
                    quota: {health[p.id].quota.used != null ? `${health[p.id].quota.used}/${health[p.id].quota.allocation} ${health[p.id].quota.unit}` : `${health[p.id].quota.allocation} ${health[p.id].quota.unit} free`}
                    {health[p.id].quota.remaining != null && <b className="text-[#30D158]"> · {health[p.id].quota.remaining} left</b>}
                  </span>
                )}
              </div>
            )}

            {(p.requires_key || p.optional_key_env) && (
              <div className="mt-3 rounded-lg border border-white/10 bg-black/20 p-2.5" data-testid={`igp-keys-editor-${p.id}`}>
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-[#666]"><KeyRound size={11} /> {p.requires_key ? "API Credentials" : "Optional API Key"}</div>
                <div className="flex flex-wrap items-end gap-2">
                  {[...(p.key_envs || []), ...(p.optional_key_env ? [p.optional_key_env] : [])].map((ev) => (
                    <div key={ev} className="flex flex-col gap-1">
                      <label className="text-[10px] text-[#A1A1AA]">{ev}</label>
                      <input type="password" autoComplete="off" placeholder={p.missing_keys?.includes(ev) ? "not set" : "•••• configured"}
                        value={keyDraft[`${p.id}.${ev}`] || ""} data-testid={`igp-keyinput-${p.id}-${ev}`}
                        onChange={(e) => setKeyDraft((prev) => ({ ...prev, [`${p.id}.${ev}`]: e.target.value }))}
                        className="w-56 rounded-lg border border-white/10 bg-[#121212] px-2 py-1.5 text-xs text-white outline-none focus:border-[#00F0FF]/50" />
                    </div>
                  ))}
                  <button onClick={() => saveKeys(p)} disabled={savingKeys === p.id} data-testid={`igp-savekeys-${p.id}`}
                    className="flex items-center gap-1.5 rounded-lg bg-[#00F0FF] px-3 py-1.5 text-xs font-bold text-black hover:bg-[#00F0FF]/85 disabled:opacity-50">
                    {savingKeys === p.id ? <Loader2 size={12} className="animate-spin" /> : <KeyRound size={12} />} Save Keys
                  </button>
                  {p.signup_url && (
                    <a href={p.signup_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 pb-1.5 text-xs font-semibold text-[#00F0FF] hover:underline">Get key <ExternalLink size={11} /></a>
                  )}
                </div>
                <p className="mt-1.5 text-[10px] text-[#666]">Stored encrypted in Secrets &amp; Configuration. Values are never shown back.</p>
              </div>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-[#666]">Model</span>
                <select value={p.model} onChange={(e) => setProvider(p.id, "model", e.target.value)} disabled={saving}
                  data-testid={`igp-model-${p.id}`} className="rounded-lg border border-white/10 bg-[#121212] px-2 py-1 text-xs text-white">
                  {Array.from(new Set([...(liveModels[p.id] || p.models), p.model])).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <button onClick={() => refreshModels(p.id)} disabled={refreshingModels === p.id} data-testid={`igp-refresh-models-${p.id}`}
                  title="Pull the provider's current live model catalog"
                  className="flex items-center gap-1 rounded-lg border border-white/15 px-2 py-1 text-[11px] font-semibold text-[#A1A1AA] hover:text-[#00F0FF] disabled:opacity-40">
                  {refreshingModels === p.id ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />} Refresh Models
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] uppercase tracking-wide text-[#666]">Priority</span>
                <input type="number" defaultValue={p.priority} min={1} max={99}
                  onBlur={(e) => { const v = parseInt(e.target.value); if (v && v !== p.priority) setProvider(p.id, "priority", v); }}
                  data-testid={`igp-priority-${p.id}`} className="w-16 rounded-lg border border-white/10 bg-[#121212] px-2 py-1 text-xs text-white" />
              </div>
              {p.requires_key && (
                <div className={`flex items-center gap-1.5 text-xs font-semibold ${p.keys_configured ? "text-[#30D158]" : "text-[#FF9F0A]"}`} data-testid={`igp-keys-${p.id}`}>
                  {p.keys_configured ? <CheckCircle2 size={13} /> : <KeyRound size={13} />}
                  {p.keys_configured ? "Keys configured" : `Missing: ${p.missing_keys.join(", ")}`}
                </div>
              )}
            </div>

            {testImg && testImg.id === p.id && (
              <div className="mt-3" data-testid={`igp-test-result-${p.id}`}>
                <img src={testImg.url} alt="test" className="h-40 w-40 rounded-lg border border-white/10 object-cover" />
              </div>
            )}
          </div>
          );
        })}
      </div>

      {guideFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="igp-guide-modal" onClick={() => setGuideFor(null)}>
          <div className="relative max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-white/10 bg-[#141414] p-5" onClick={(e) => e.stopPropagation()}>
            <WindowSearch />
            <div className="mb-3 flex items-start justify-between">
              <div>
                <h3 className="font-display text-lg font-black text-white">{guideFor.name} — Setup Guide</h3>
                <span className={`text-xs font-bold ${guideFor.cost_tier === "free" ? "text-[#30D158]" : "text-[#FF9F0A]"}`}>{guideFor.guide?.cost || guideFor.cost_tier.toUpperCase()}</span>
              </div>
              <button onClick={() => setGuideFor(null)} data-testid="igp-guide-close" className="rounded-lg p-1 text-[#A1A1AA] hover:text-white"><X size={18} /></button>
            </div>
            {guideFor.guide?.steps ? (
              <ol className="space-y-2.5">
                {guideFor.guide.steps.map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm text-[#D4D4D8]">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#8B5CF6]/20 text-xs font-bold text-[#B794F6]">{i + 1}</span>
                    <span className="pt-0.5">{s}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="text-sm text-[#A1A1AA]">No guide available.</p>}
            {guideFor.signup_url && (
              <a href={guideFor.signup_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#8B5CF6] px-3 py-2 text-xs font-bold text-white hover:bg-[#8B5CF6]/85">
                Open provider dashboard <ExternalLink size={12} />
              </a>
            )}
          </div>
        </div>
      )}

      <CustomProviders />
    </div>
  );
}
